import React, { useEffect, useMemo, useState, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Modal from "react-modal";
import { getApi } from "../../../Services/apiService";
import { renderNameWithColor } from "../../../Utils/colors";
import { calculateAge } from "../../../Utils/family-tree/dateAndTime";
import { toast } from "react-toastify";
import { getFamilyTreeIdFromStorage } from "../../../Utils/headOfLineRole";
import { formatDashboardFamilyLineHeadName } from "../../../Utils/dashboardMemberDisplayName";
import { formatSuffix, getMemberSuffix } from "../../../Utils/name";
import {
  FilterTriggerLabel,
  filterTriggerClass,
  formatFilterValueList,
} from "../../../Utils/filterTriggerLabel";
import {
  convertUTCToDateInvitation,
  convertUTCToTime12Hour,
} from "../../../Utils/dateUtils";
import SpecialInstructionsDisplay from "./SpecialInstructionsDisplay";
import { isFuneralEvent, FuneralHostLine } from "./funeralFlyer";

const AllTreeEvents = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const eventsListType = location.state?.eventsListType ?? 0;
  const archivedEventsFromState = location.state?.archivedEvents ?? null;
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortColumn, setSortColumn] = useState("invitee"); // 'host' | 'creator' | 'invitee' | 'eventName' | 'eventDate' | 'rsvp' | 'status'
  const [sortOrder, setSortOrder] = useState("asc"); // 'asc' | 'desc'
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showEventModal, setShowEventModal] = useState(false);

  const [showAgeRange, setShowAgeRange] = useState(false);
  const [ageFrom, setAgeFrom] = useState("");
  const [ageTo, setAgeTo] = useState("");
  const [appliedAgeFrom, setAppliedAgeFrom] = useState(null);
  const [appliedAgeTo, setAppliedAgeTo] = useState(null);

  const [showFamilyColor, setShowFamilyColor] = useState(false);
  const [familyColorList, setFamilyColorList] = useState([]);
  const [selectedFamilyColor, setSelectedFamilyColor] = useState([]);
  const [appliedFamilyColors, setAppliedFamilyColors] = useState([]);

  const [hostFilter, setHostFilter] = useState("");
  const [creatorFilter, setCreatorFilter] = useState("");
  const [inviteeFilter, setInviteeFilter] = useState("");
  const [rsvpFilter, setRsvpFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const [showHostFilter, setShowHostFilter] = useState(false);
  const [showCreatorFilter, setShowCreatorFilter] = useState(false);
  const [showInviteeFilter, setShowInviteeFilter] = useState(false);
  const [showRsvpFilter, setShowRsvpFilter] = useState(false);
  const [showStatusFilter, setShowStatusFilter] = useState(false);

  const ageRangeRef = useRef(null);
  const famRef = useRef(null);
  const hostFilterRef = useRef(null);
  const creatorFilterRef = useRef(null);
  const inviteeFilterRef = useRef(null);
  const rsvpFilterRef = useRef(null);
  const statusFilterRef = useRef(null);
  const tableScrollRef = useRef(null);

  const formatMemberName = (host) => {
    let firstName =
      host.first_name || (host.name ? host.name.trim().split(" ")[0] : "");
    if (!firstName) firstName = "Unnamed";
    const suffix = host.suffix || "";
    const specialName = host.special_name || "";

    if (suffix) {
      const suffixUpper = suffix.trim().toUpperCase();
      const suffixWithPeriod =
        suffixUpper === "SR" || suffixUpper === "JR"
          ? `${suffix.trim()}.`
          : suffix.trim();
      return `${firstName}, ${suffixWithPeriod}`;
    }
    if (specialName) {
      return `${firstName} ${specialName}`;
    }
    return firstName;
  };

  const formatHostName = (event) => {
    if (event?.Host) return formatMemberName(event.Host);
    if (event?.host_name) {
      return event.host_name.split(" ")[0] || event.host_name;
    }
    return "-";
  };

  const getAgeFromMemberDetails = useCallback((memberDetails) => {
    if (!memberDetails) return -1;
    if (memberDetails.age !== undefined && memberDetails.age !== null) {
      const a = parseInt(memberDetails.age);
      if (!isNaN(a) && a >= 0) return a;
    }
    const dob = memberDetails.dob ?? memberDetails.date_of_birth ?? "";
    if (!dob) return -1;
    let dobVal = dob;
    if (typeof dob === "number" || (typeof dob === "string" && /^-?\d+$/.test(dob))) {
      dobVal = new Date(parseInt(dob)).toISOString();
    }
    const ageStr = calculateAge(dobVal);
    if (!ageStr) return -1;
    return parseInt(ageStr) || -1;
  }, []);

  const fetchFamilyColorData = useCallback(async () => {
    try {
      const family_tree_id_value = getFamilyTreeIdFromStorage();
      if (!family_tree_id_value) return;
      const res = await getApi(`/family-members-count-by-filter?family_tree_id=${family_tree_id_value}&type=${0}`);
      setFamilyColorList(res?.data?.members || []);
    } catch (err) {
      console.error(err);
      setFamilyColorList([]);
    }
  }, []);

  const getMemberAge = useCallback((row) => {
    if (row?.age !== undefined && row?.age !== null) {
      const a = parseInt(row.age);
      if (!isNaN(a) && a >= 0) return a;
    }
    if (row?.dob !== undefined && row?.dob !== null) {
      let dob = row.dob;
      if (typeof dob === "number" || (typeof dob === "string" && /^-?\d+$/.test(dob))) {
        dob = new Date(parseInt(dob)).toISOString();
      }
      const ageStr = calculateAge(dob);
      if (ageStr) return parseInt(ageStr) || -1;
    }
    return -1;
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ageRangeRef.current && !ageRangeRef.current.contains(e.target)) {
        setShowAgeRange(false);
      }
      if (famRef.current && !famRef.current.contains(e.target)) {
        setShowFamilyColor(false);
      }
      if (hostFilterRef.current && !hostFilterRef.current.contains(e.target)) {
        setShowHostFilter(false);
      }
      if (creatorFilterRef.current && !creatorFilterRef.current.contains(e.target)) {
        setShowCreatorFilter(false);
      }
      if (inviteeFilterRef.current && !inviteeFilterRef.current.contains(e.target)) {
        setShowInviteeFilter(false);
      }
      if (rsvpFilterRef.current && !rsvpFilterRef.current.contains(e.target)) {
        setShowRsvpFilter(false);
      }
      if (statusFilterRef.current && !statusFilterRef.current.contains(e.target)) {
        setShowStatusFilter(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const fetchAllTreeEvents = async () => {
    try {
      setLoading(true);

      const family_tree_id = getFamilyTreeIdFromStorage();

      if (!family_tree_id) {
        setError("Family tree not found.");
        setData([]);
        return;
      }

      const params = new URLSearchParams();
      params.append("family_tree_id", family_tree_id);
      params.append("type", String(eventsListType));

      if (hostFilter) {
        params.append("host", hostFilter);
      }
      if (creatorFilter) {
        params.append("creator", creatorFilter);
      }
      if (inviteeFilter) {
        params.append("invitee", inviteeFilter);
      }
      if (statusFilter) {
        let statusValue = statusFilter;
        const lower = statusFilter.toLowerCase();
        if (lower === "accepted") statusValue = "1";
        else if (lower === "decline") statusValue = "2";
        else if (lower === "pending") statusValue = "0";
        params.append("status", statusValue);
      }

      const response = await getApi(`/all-tree-events?${params.toString()}`);

      if (response?.statusCode !== 200) {
        setData([]);
        setError(null);
        return;
      }

      const rawData = response?.data;
      if (!rawData || typeof rawData !== "object") {
        setData([]);
        setError(null);
        return;
      }

      const eventsList = Array.isArray(rawData) ? rawData : Object.values(rawData);
      const rows = [];

      eventsList.forEach((event) => {
        const eventTitle = event?.title || "-";
        const archivedEvent =
          eventsListType === 2 &&
          archivedEventsFromState?.length &&
          archivedEventsFromState.find((e) => String(e.id) === String(event.id));
        const eventDocument =
          event?.document || archivedEvent?.document || null;
        const eventDetailsForRow = archivedEvent
          ? { ...event, ...archivedEvent }
          : event;
        const rsvpDeadline = event?.Rsvp_deadline ?? eventDetailsForRow?.Rsvp_deadline ?? null;
        const invited = event?.invited_members || eventDetailsForRow?.invited_members || [];
        const host = event?.Host ?? eventDetailsForRow?.Host;
        const hostName = host
          ? [host.first_name, host.last_name].filter(Boolean).join(" ") || host.first_name || host.last_name || "-"
          : "-";

        // Try to derive creator details from invited_members using event.user_id
        const creatorInvitation = invited.find(
          (inv) => inv?.user_id && event?.user_id && inv.user_id === event.user_id
        );
        const creatorMemberDetails = creatorInvitation?.member_details || null;

        const hostId = host?.id ?? null;
        const creatorUserId = event?.user_id ?? null;

        invited.forEach((inv) => {
          const memberDetails = inv?.member_details || {};
          const user = inv?.User || {};
          const inviteeUserId = inv?.user_id ?? user?.id ?? null;
          const age = getAgeFromMemberDetails(memberDetails);
          rows.push({
            id: inv.id || `${event.id}-${user.id}`,
            first_name: memberDetails.first_name || (user.name ? user.name.trim().split(" ")[0] : "") || "Unnamed",
            last_name: memberDetails.last_name,
            suffix: memberDetails.suffix,
            // Full name too, so a suffix only folded into the name still shows
            name: memberDetails.name || user.name || "",
            special_name: memberDetails.special_name,
            color_name: memberDetails.color_name,
            hex_code: memberDetails.hex_code || "#000000",
            host_first_name: host?.first_name,
            host_suffix: host?.suffix,
            host_special_name: host?.special_name,
            host_color_name: host?.color_name,
            host_hex_code: host?.hex_code,
            host_id: hostId,
            creator_user_id: creatorUserId,
            invitee_user_id: inviteeUserId,
            creator_name: (event?.Creator ?? event?.creator)?.name ?? null,
            creator_first_name: creatorMemberDetails?.first_name,
            creator_last_name: creatorMemberDetails?.last_name,
            creator_suffix: creatorMemberDetails?.suffix,
            creator_special_name: creatorMemberDetails?.special_name,
            creator_color_name: creatorMemberDetails?.color_name,
            creator_hex_code: creatorMemberDetails?.hex_code,
            Rsvp_deadline: rsvpDeadline,
            invitation_status: inv.invitation_status,
            host_name: hostName,
            event_name: eventTitle,
            event_link: eventDocument,
            eventDetails: eventDetailsForRow,
            age,
            dob: memberDetails.dob ?? memberDetails.date_of_birth,
          });
        });
      });

      setData(rows);
      setError(null);
    } catch (err) {
      console.error("Error fetching all-tree-events:", err);
      setError("Error connecting to server");
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllTreeEvents();
  }, [hostFilter, creatorFilter, inviteeFilter, statusFilter, eventsListType]);

  // Format backend date/time values using UTC (no local timezone conversion)
  const formatDateShort = convertUTCToDateInvitation;
  const formatTime12Hour = convertUTCToTime12Hour;

  const renderStatusBadge = (status) => {
    switch (status) {
      case 1:
      case "accepted":
        return <span className="status-accepted">Accepted</span>;
      case 2:
      case "declined":
        return <span className="status-decline">Decline</span>;
      case 0:
      case "pending":
      default:
        return <span className="status-pending">Pending</span>;
    }
  };

  const formatPersonName = (row) => {
    let firstName =
      row?.first_name ||
      row?.member_first_name ||
      (row?.name ? row.name.trim().split(" ")[0] : "");
    if (!firstName) {
      firstName = "Unnamed";
    }

    const suffix = getMemberSuffix(row);
    const specialName = row?.special_name || row?.member_special_name || "";

    if (suffix) {
      return `${firstName}, ${formatSuffix(suffix)}`;
    } else if (specialName) {
      return `${firstName} ${specialName}`;
    }
    return firstName;
  };

  const getHeadOfLineName = formatDashboardFamilyLineHeadName;

  const getHostFilterLabel = (row) =>
    formatPersonName({
      first_name: row?.host_first_name,
      member_first_name: row?.host_first_name,
      name: row?.host_first_name || "",
      suffix: row?.host_suffix,
      special_name: row?.host_special_name,
    }) || row?.host_name || "-";

  const getCreatorFilterLabel = (row) =>
    row?.creator_name ||
    formatPersonName({
      first_name: row?.creator_first_name,
      member_first_name: row?.creator_first_name,
      name:
        row?.creator_first_name && row?.creator_last_name
          ? `${row.creator_first_name} ${row.creator_last_name}`
          : row?.creator_first_name || "",
      suffix: row?.creator_suffix,
      special_name: row?.creator_special_name,
    }) ||
    "";

  /** Matches the Event Creator table cell: Admin vs N/A from Creator.is_admin (must match sort keys). */
  const getEventCreatorColumnLabel = (row) => {
    const eventDetails = row?.eventDetails || {};
    const creatorObj = eventDetails?.Creator ?? eventDetails?.creator;
    return creatorObj?.is_admin === true ? "Admin" : "N/A";
  };

  const getInviteeFilterLabel = (row) => formatPersonName(row) || "";

  const getRsvpFilterLabel = (row) =>
    row?.Rsvp_deadline ? formatDateShort(row.Rsvp_deadline) : "";

  const getStatusLabel = (row) => {
    const status = row?.invitation_status;
    switch (status) {
      case 1:
      case "accepted":
        return "Accepted";
      case 2:
      case "declined":
        return "Decline";
      case 0:
      case "pending":
      default:
        return "Pending";
    }
  };

  const sortedRows = useMemo(() => {
    let rows = [...(data || [])];

    if (appliedFamilyColors.length > 0) {
      rows = rows.filter((row) => appliedFamilyColors.includes(row?.hex_code));
    }
    if (appliedAgeFrom != null && appliedAgeTo != null) {
      rows = rows.filter((row) => {
        const age = getMemberAge(row);
        if (age < 0) return true;
        return age >= appliedAgeFrom && age <= appliedAgeTo;
      });
    }

    if (hostFilter) {
      rows = rows.filter((row) => String(row?.host_id) === String(hostFilter));
    }
    if (creatorFilter) {
      rows = rows.filter((row) => String(row?.creator_user_id) === String(creatorFilter));
    }
    if (inviteeFilter) {
      rows = rows.filter((row) => String(row?.invitee_user_id) === String(inviteeFilter));
    }
    if (rsvpFilter) {
      rows = rows.filter((row) => getRsvpFilterLabel(row) === rsvpFilter);
    }
    if (statusFilter) {
      rows = rows.filter((row) => getStatusLabel(row) === statusFilter);
    }

    rows.sort((a, b) => {
      let aVal;
      let bVal;

      switch (sortColumn) {
        case "host":
          aVal = (getHostFilterLabel(a) || "").toLowerCase();
          bVal = (getHostFilterLabel(b) || "").toLowerCase();
          break;
        case "creator":
          aVal = getEventCreatorColumnLabel(a).toLowerCase();
          bVal = getEventCreatorColumnLabel(b).toLowerCase();
          break;
        case "rsvp": {
          const aTs = Number(a?.Rsvp_deadline) || 0;
          const bTs = Number(b?.Rsvp_deadline) || 0;
          aVal = aTs;
          bVal = bTs;
          break;
        }
        case "eventName":
          aVal = (a?.event_name || "").toLowerCase();
          bVal = (b?.event_name || "").toLowerCase();
          break;
        case "eventDate": {
          const aTs = Number(a?.eventDetails?.start_date) || 0;
          const bTs = Number(b?.eventDetails?.start_date) || 0;
          aVal = aTs;
          bVal = bTs;
          break;
        }
        case "status":
          aVal = (getStatusLabel(a) || "").toLowerCase();
          bVal = (getStatusLabel(b) || "").toLowerCase();
          break;
        case "invitee":
        default:
          aVal = (formatPersonName(a) || "").toLowerCase();
          bVal = (formatPersonName(b) || "").toLowerCase();
          break;
      }

      if (aVal < bVal) return sortOrder === "asc" ? -1 : 1;
      if (aVal > bVal) return sortOrder === "asc" ? 1 : -1;
      // When group sort is Host: auto-sort Invitees by first name within each host group
      if (sortColumn === "host") {
        const aInviteeFirst = (a?.first_name || a?.member_first_name || formatPersonName(a) || "").toLowerCase();
        const bInviteeFirst = (b?.first_name || b?.member_first_name || formatPersonName(b) || "").toLowerCase();
        if (aInviteeFirst < bInviteeFirst) return sortOrder === "asc" ? -1 : 1;
        if (aInviteeFirst > bInviteeFirst) return sortOrder === "asc" ? 1 : -1;
      }
      // When group sort is Invitees: auto-sort Host by name within each invitee group
      if (sortColumn === "invitee") {
        const aHost = (getHostFilterLabel(a) || "").toLowerCase();
        const bHost = (getHostFilterLabel(b) || "").toLowerCase();
        if (aHost < bHost) return sortOrder === "asc" ? -1 : 1;
        if (aHost > bHost) return sortOrder === "asc" ? 1 : -1;
      }
      if (sortColumn === "creator") {
        const aInviteeFirst = (a?.first_name || a?.member_first_name || formatPersonName(a) || "").toLowerCase();
        const bInviteeFirst = (b?.first_name || b?.member_first_name || formatPersonName(b) || "").toLowerCase();
        if (aInviteeFirst < bInviteeFirst) return sortOrder === "asc" ? -1 : 1;
        if (aInviteeFirst > bInviteeFirst) return sortOrder === "asc" ? 1 : -1;
      }
      return 0;
    });
    return rows;
  }, [data, sortColumn, sortOrder, appliedFamilyColors, appliedAgeFrom, appliedAgeTo, getMemberAge, hostFilter, creatorFilter, inviteeFilter, rsvpFilter, statusFilter]);

  /** Totals for the rows currently shown (respects every filter). */
  const statusTotals = useMemo(() => {
    const totals = { Accepted: 0, Pending: 0, Decline: 0 };
    sortedRows.forEach((row) => {
      totals[getStatusLabel(row)] += 1;
    });
    return totals;
  }, [sortedRows]);

  // Mobile: the Invitees column is rendered as the LAST column and pinned to
  // the right edge of the scroll port. It has to be last for that to work: a
  // sticky cell keeps its slot in the row, so pinning it from its desktop
  // position (3rd) leaves that slot behind as a blank gap once it sticks.
  const [isMobileLayout, setIsMobileLayout] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(max-width: 768px)").matches
  );
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 768px)");
    const apply = () => setIsMobileLayout(mq.matches);
    apply();
    mq.addEventListener("change", apply);
    return () => mq.removeEventListener("change", apply);
  }, []);

  const hostOptions = useMemo(() => {
    const map = new Map();
    (data || []).forEach((row) => {
      const id = row?.host_id;
      if (id == null || id === "") return;
      const name = getHostFilterLabel(row);
      const hex = row?.host_hex_code || row?.hex_code || "#000000";
      const lineLabel = row?.host_color_name
        ? `${row.host_color_name} Line`
        : row?.color_name
        ? `${row.color_name} Line`
        : "";
      if (!map.has(String(id))) {
        map.set(String(id), { id: String(id), name, hex, lineLabel });
      }
    });
    return Array.from(map.values()).sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  }, [data]);

  const creatorOptions = useMemo(() => {
    const map = new Map();
    (data || []).forEach((row) => {
      const id = row?.creator_user_id;
      if (id == null || id === "") return;
      const name = getCreatorFilterLabel(row);
      if (!map.has(String(id))) {
        map.set(String(id), { id: String(id), name: name || "Unnamed" });
      }
    });
    return Array.from(map.values()).sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  }, [data]);

  const inviteeOptions = useMemo(() => {
    const map = new Map();
    (data || []).forEach((row) => {
      const id = row?.invitee_user_id;
      if (id == null || id === "") return;
      const name = getInviteeFilterLabel(row);
      const hex = row?.hex_code || "#000000";
      const lineLabel = row?.color_name ? `${row.color_name} Line` : "";
      if (!map.has(String(id))) {
        map.set(String(id), { id: String(id), name, hex, lineLabel });
      }
    });
    return Array.from(map.values()).sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  }, [data]);

  const rsvpOptions = useMemo(() => {
    const set = new Set();
    (data || []).forEach((row) => {
      const label = getRsvpFilterLabel(row);
      if (label) set.add(label);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [data]);

  const statusOptions = useMemo(() => {
    const set = new Set();
    (data || []).forEach((row) => {
      const label = getStatusLabel(row);
      if (label) set.add(label);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [data]);

  const selectedHostOption = useMemo(
    () => hostOptions.find((opt) => String(opt.id) === String(hostFilter)) || null,
    [hostOptions, hostFilter]
  );

  const selectedCreatorOption = useMemo(
    () => creatorOptions.find((opt) => String(opt.id) === String(creatorFilter)) || null,
    [creatorOptions, creatorFilter]
  );

  const selectedInviteeOption = useMemo(
    () => inviteeOptions.find((opt) => String(opt.id) === String(inviteeFilter)) || null,
    [inviteeOptions, inviteeFilter]
  );

  const handleSort = (column) => {
    setSortColumn((prevCol) => {
      if (prevCol === column) {
        setSortOrder((prevOrder) => (prevOrder === "asc" ? "desc" : "asc"));
        return prevCol;
      }
      setSortOrder("asc");
      return column;
    });
  };

  const handleEventLinkClick = (e, row) => {
    e.preventDefault();
    if (!row?.eventDetails) return;
    setSelectedEvent(row.eventDetails);
    setShowEventModal(true);
  };

  const handleAgeFromChange = (e) => setAgeFrom(e.target.value);
  const handleAgeToChange = (e) => setAgeTo(e.target.value);

  const handleApplyAgeRange = () => {
    if (!ageFrom || !ageTo) {
      toast.info("Please enter both From and To age values");
      return;
    }
    const fromAge = parseInt(ageFrom);
    const toAge = parseInt(ageTo);
    if (isNaN(fromAge) || isNaN(toAge)) {
      toast.info("Please enter valid age numbers");
      return;
    }
    if (toAge <= fromAge) {
      toast.info("To age must be greater than From age");
      return;
    }
    setAppliedAgeFrom(fromAge);
    setAppliedAgeTo(toAge);
    setShowAgeRange(false);
    toast.success("Filtered by Age Range");
  };

  const handleClearAgeRange = () => {
    setAgeFrom("");
    setAgeTo("");
    setAppliedAgeFrom(null);
    setAppliedAgeTo(null);
    setShowAgeRange(false);
    toast.success("Age range filter cleared");
  };

  const handleApplyFamilyColor = () => {
    if (!selectedFamilyColor || selectedFamilyColor.length === 0) {
      toast.info("Please select a family color");
      return;
    }
    setAppliedFamilyColors([...selectedFamilyColor]);
    setShowFamilyColor(false);
    toast.success("Filtered by Family Color");
  };

  const handleClearFamilyColor = () => {
    setSelectedFamilyColor([]);
    setAppliedFamilyColors([]);
    setShowFamilyColor(false);
    toast.success("Family color filter cleared");
  };

  const handleClearAllFilters = () => {
    setHostFilter("");
    setCreatorFilter("");
    setInviteeFilter("");
    setRsvpFilter("");
    setStatusFilter("");
    setSelectedFamilyColor([]);
    setAppliedFamilyColors([]);
    setShowFamilyColor(false);
  };

  if (loading) {
    return <div>Loading events...</div>;
  }

  if (error) {
    return <div>{error}</div>;
  }

  const inviteesHeader = (
    <th
      className="col-invitees"
      onClick={() => handleSort("invitee")}
      style={{
        cursor: "pointer",
        userSelect: "none",
      }}
    >
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "6px",
          fontWeight: "600",
        }}
      >
        <span>Invitees</span>
        <span
          style={{
            fontSize: "12px",
            color: sortColumn === "invitee" ? "#025FEB" : "#999",
            lineHeight: "1",
          }}
        >
          {sortColumn === "invitee" ? (sortOrder === "asc" ? "↑" : "↓") : "↕"}
        </span>
      </span>
    </th>
  );

  return (
    <div className="family-tree-container all-tree-events-page">
      <div className="events-content all-tree-events-page-content">
        <div
          className="all-tree-events-filters"
          style={{
            display: "flex",
            gap: "12px",
            alignItems: "center",
            marginBottom: "12px",
            flexWrap: "wrap",
          }}
        >
          <button
            onClick={() => navigate("/events")}
            className="dashboard-detail-back-btn"
            aria-label="Back to Events"
            style={{ marginRight: "8px" }}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="dashboard-detail-back-icon"
            >
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
          </button>
          {/* Mobile-only page title, sits beside the back arrow */}
          <h2 className="all-tree-events-mobile-title">Event Detail</h2>
          {/* Age Range filter disabled for now */}
            <div ref={famRef} className="selctions-option position-relative">
              <div
                className={filterTriggerClass(
                  "selection-drop family-color-drop",
                  appliedFamilyColors.length > 0
                )}
                style={{ fontWeight: "bold" }}
                onClick={(e) => {
                  e.stopPropagation();
                  setShowFamilyColor((prev) => {
                    const next = !prev;
                    if (next) fetchFamilyColorData();
                    return next;
                  });
                }}
              >
                <FilterTriggerLabel
                  label="Line/Color"
                  value={
                    appliedFamilyColors.length > 0
                      ? formatFilterValueList(
                          appliedFamilyColors.map((hex) => {
                            const entry = familyColorList.find((c) => c?.hex_code === hex);
                            return entry?.color_name || entry?.name || "";
                          })
                        ) || `${appliedFamilyColors.length} selected`
                      : ""
                  }
                />
              </div>
              {showFamilyColor && (
                <div className="member-list" style={{ width: "300px", minWidth: "200px" }}>
                  {familyColorList.length === 0 ? (
                    <p>There is no family color</p>
                  ) : (
                    familyColorList.map((color, index) => {
                      const headOfLineName = formatDashboardFamilyLineHeadName(color);
                      const isChecked = selectedFamilyColor.includes(color.hex_code);
                      return (
                        <div className="member-item" key={index}>
                          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedFamilyColor([...selectedFamilyColor, color.hex_code]);
                                } else {
                                  setSelectedFamilyColor(selectedFamilyColor.filter((hex) => hex !== color.hex_code));
                                }
                              }}
                            />
                            <div style={{ flex: "1" }}>
                              {renderNameWithColor(
                                headOfLineName || "Start/Top of Line",
                                color?.hex_code || "#000000",
                                color?.color_name ? `${color.color_name} Line` : color?.name ? `${color.name} Line` : ""
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                  <div className="btn-container" style={{ display: "flex", gap: "8px", marginTop: "10px", paddingTop: "10px", borderTop: "1px solid #e0e0e0" }}>
                    <button type="button" className="btn btn-clear" onClick={handleClearFamilyColor} style={{ flex: "1", padding: "6px 12px", fontSize: "14px", borderRadius: "4px" }}>Clear</button>
                    <button type="button" className="btn btn-apply" onClick={handleApplyFamilyColor} style={{ flex: "1", padding: "6px 12px", fontSize: "14px", borderRadius: "4px" }}>Apply</button>
                  </div>
                </div>
              )}
            </div>

            <div
              ref={hostFilterRef}
              className="selctions-option position-relative"
              style={{ minWidth: "280px" }}
            >
              <div
                className="selection-drop text-nowrap"
                style={{
                  fontWeight: "bold",
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  overflow: "hidden",
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  setShowHostFilter((prev) => !prev);
                }}
              >
                <span style={{ flexShrink: 0 }}>Host:</span>
                {selectedHostOption ? (
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "4px",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    <span>{selectedHostOption.name}</span>
                    {selectedHostOption.lineLabel && (
                      <>
                        <span
                          style={{
                            display: "inline-block",
                            width: "7px",
                            height: "7px",
                            borderRadius: "50%",
                            backgroundColor: selectedHostOption.hex,
                            marginInline: "2px",
                          }}
                        />
                        <span style={{ color: selectedHostOption.hex }}>
                          {selectedHostOption.lineLabel}
                        </span>
                      </>
                    )}
                  </span>
                ) : (
                  <span>All</span>
                )}
              </div>
              {showHostFilter && (
                <div className="member-list" style={{ width: "100%" }}>
                  <div
                    className="member-item cursor-pointer"
                    style={{ cursor: "pointer" }}
                    onClick={() => {
                      setHostFilter("");
                      setShowHostFilter(false);
                    }}
                  >
                    Host: All
                  </div>
                  {hostOptions.map((opt) => (
                    <div
                      key={opt.id}
                      className="member-item cursor-pointer"
                      style={{ cursor: "pointer" }}
                      onClick={() => {
                        setHostFilter(opt.id);
                        setShowHostFilter(false);
                      }}
                    >
                      {renderNameWithColor(opt.name, opt.hex, opt.lineLabel)}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div
              ref={creatorFilterRef}
              className="selctions-option position-relative"
              style={{ minWidth: "190px" }}
            >
              <div
                className="selection-drop text-nowrap"
                style={{ fontWeight: "bold", width: "100%" }}
                onClick={(e) => {
                  e.stopPropagation();
                  setShowCreatorFilter((prev) => !prev);
                }}
              >
                {selectedCreatorOption ? `Event Creator: ${selectedCreatorOption.name}` : "Event Creator: All"}
              </div>
              {showCreatorFilter && (
                <div className="member-list" style={{ width: "100%" }}>
                  <div
                    className="member-item cursor-pointer"
                    style={{ cursor: "pointer" }}
                    onClick={() => {
                      setCreatorFilter("");
                      setShowCreatorFilter(false);
                    }}
                  >
                    Event Creator: All
                  </div>
                  {creatorOptions.map((opt) => (
                    <div
                      key={opt.id}
                      className="member-item cursor-pointer"
                      style={{ cursor: "pointer" }}
                      onClick={() => {
                        setCreatorFilter(opt.id);
                        setShowCreatorFilter(false);
                      }}
                    >
                      {opt.name}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div
              ref={inviteeFilterRef}
              className="selctions-option position-relative"
              style={{ minWidth: "240px" }}
            >
              <div
                className="selection-drop text-nowrap"
                style={{
                  fontWeight: "bold",
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  overflow: "hidden",
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  setShowInviteeFilter((prev) => !prev);
                }}
              >
                <span style={{ flexShrink: 0 }}>Invitees:</span>
                {selectedInviteeOption ? (
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "4px",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    <span>{selectedInviteeOption.name}</span>
                    {selectedInviteeOption.lineLabel && (
                      <>
                        <span
                          style={{
                            display: "inline-block",
                            width: "7px",
                            height: "7px",
                            borderRadius: "50%",
                            backgroundColor: selectedInviteeOption.hex,
                            marginInline: "2px",
                          }}
                        />
                        <span style={{ color: selectedInviteeOption.hex }}>
                          {selectedInviteeOption.lineLabel}
                        </span>
                      </>
                    )}
                  </span>
                ) : (
                  <span>All</span>
                )}
              </div>
              {showInviteeFilter && (
                <div className="member-list" style={{ width: "100%" }}>
                  <div
                    className="member-item cursor-pointer"
                    style={{ cursor: "pointer" }}
                    onClick={() => {
                      setInviteeFilter("");
                      setShowInviteeFilter(false);
                    }}
                  >
                    Invitees: All
                  </div>
                  {inviteeOptions.map((opt) => (
                    <div
                      key={opt.id}
                      className="member-item cursor-pointer"
                      style={{ cursor: "pointer" }}
                      onClick={() => {
                        setInviteeFilter(opt.id);
                        setShowInviteeFilter(false);
                      }}
                    >
                      {renderNameWithColor(opt.name, opt.hex, opt.lineLabel)}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* RSVP Deadline filter temporarily disabled */}

            <div
              ref={statusFilterRef}
              className="selctions-option position-relative"
              style={{ minWidth: "140px" }}
            >
              <div
                className="selection-drop text-nowrap"
                style={{ fontWeight: "bold", width: "100%" }}
                onClick={(e) => {
                  e.stopPropagation();
                  setShowStatusFilter((prev) => !prev);
                }}
              >
                {statusFilter ? `Status: ${statusFilter}` : "Status: All"}
              </div>
              {showStatusFilter && (
                <div className="member-list" style={{ width: "100%" }}>
                  <div
                    className="member-item cursor-pointer"
                    style={{ cursor: "pointer" }}
                    onClick={() => {
                      setStatusFilter("");
                      setShowStatusFilter(false);
                    }}
                  >
                    Status: All
                  </div>
                  {statusOptions.map((opt) => (
                    <div
                      key={opt}
                      className="member-item cursor-pointer"
                      style={{ cursor: "pointer" }}
                      onClick={() => {
                        setStatusFilter(opt);
                        setShowStatusFilter(false);
                      }}
                    >
                      {opt}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div
              className="selctions-option position-relative all-tree-events-clear-filters-wrap"
              style={{ flexShrink: 0 }}
            >
              <div
                className="selection-drop text-nowrap all-tree-events-clear-filters-btn"
                style={{
                  fontWeight: "bold",
                  cursor: "pointer",
                  textAlign: "center",
                  backgroundImage: "none",
                  padding: "6px 10px",
                  width: "auto",
                  minWidth: "fit-content",
                  boxSizing: "border-box",
                }}
                onClick={handleClearAllFilters}
              >
                Clear Filters
              </div>
            </div>
        </div>
        <div className="all-tree-events-totals">
          <span>
            Total Invitations: <strong>{sortedRows.length}</strong>
          </span>
          <span className="status-accepted">
            Accepted: <strong>{statusTotals.Accepted}</strong>
          </span>
          <span className="status-pending">
            Pending: <strong>{statusTotals.Pending}</strong>
          </span>
          <span className="status-decline">
            Declined: <strong>{statusTotals.Decline}</strong>
          </span>
        </div>
        <div className="customer-tablle-main all-tree-events-table-wrapper">
          <div className="all-tree-events-table-scroll" ref={tableScrollRef}>
          <table>
            <thead>
              <tr>
                <th
                  className="col-host"
                  onClick={() => handleSort("host")}
                  style={{ cursor: "pointer", userSelect: "none" }}
                >
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "6px",
                      fontWeight: "600",
                    }}
                  >
                    <span>Host</span>
                    <span
                      style={{
                        fontSize: "12px",
                        color: sortColumn === "host" ? "#025FEB" : "#999",
                        lineHeight: "1",
                      }}
                    >
                      {sortColumn === "host" ? (sortOrder === "asc" ? "↑" : "↓") : "↕"}
                    </span>
                  </span>
                </th>
                <th
                  className="col-creator"
                  onClick={() => handleSort("creator")}
                  style={{ cursor: "pointer", userSelect: "none" }}
                >
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "6px",
                      fontWeight: "600",
                    }}
                  >
                    <span>Event Creator</span>
                    <span
                      style={{
                        fontSize: "12px",
                        color: sortColumn === "creator" ? "#025FEB" : "#999",
                        lineHeight: "1",
                      }}
                    >
                      {sortColumn === "creator" ? (sortOrder === "asc" ? "↑" : "↓") : "↕"}
                    </span>
                  </span>
                </th>
                {!isMobileLayout && inviteesHeader}
                <th
                  className="col-event-name"
                  onClick={() => handleSort("eventName")}
                  style={{
                    cursor: "pointer",
                    userSelect: "none",
                    fontWeight: "600",
                    transform: "translateX(-8px)",
                  }}
                >
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "6px",
                    }}
                  >
                    <span>Event Name</span>
                    <span
                      style={{
                        fontSize: "12px",
                        color: sortColumn === "eventName" ? "#025FEB" : "#999",
                        lineHeight: "1",
                      }}
                    >
                      {sortColumn === "eventName" ? (sortOrder === "asc" ? "↑" : "↓") : "↕"}
                    </span>
                  </span>
                </th>
                <th
                  className="col-event-date"
                  onClick={() => handleSort("eventDate")}
                  style={{
                    cursor: "pointer",
                    userSelect: "none",
                    fontWeight: "600",
                    transform: "translateX(-32px)",
                  }}
                >
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "6px",
                    }}
                  >
                    <span>Event Date</span>
                    <span
                      style={{
                        fontSize: "12px",
                        color: sortColumn === "eventDate" ? "#025FEB" : "#999",
                        lineHeight: "1",
                      }}
                    >
                      {sortColumn === "eventDate" ? (sortOrder === "asc" ? "↑" : "↓") : "↕"}
                    </span>
                  </span>
                </th>
                <th className="col-event-link" style={{ fontWeight: "600" }}>
                  Event Link
                </th>
                <th
                  className="col-rsvp"
                  style={{ cursor: "pointer", userSelect: "none" }}
                  onClick={() => handleSort("rsvp")}
                >
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "6px",
                    }}
                  >
                    <span style={{ fontWeight: "600" }}>RSVP Deadline</span>
                    <span
                      style={{
                        fontSize: "12px",
                        color: sortColumn === "rsvp" ? "#025FEB" : "#999",
                        lineHeight: "1",
                      }}
                    >
                      {sortColumn === "rsvp" ? (sortOrder === "asc" ? "↑" : "↓") : "↕"}
                    </span>
                  </span>
                </th>
                <th
                  className="col-status"
                  style={{ cursor: "pointer", userSelect: "none" }}
                  onClick={() => handleSort("status")}
                >
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "6px",
                      justifyContent: "center",
                    }}
                  >
                    <span style={{ fontWeight: "600" }}>Status</span>
                    <span
                      style={{
                        fontSize: "12px",
                        color: sortColumn === "status" ? "#025FEB" : "#999",
                        lineHeight: "1",
                      }}
                    >
                      {sortColumn === "status" ? (sortOrder === "asc" ? "↑" : "↓") : "↕"}
                    </span>
                  </span>
                </th>
                {isMobileLayout && inviteesHeader}
                {!isMobileLayout && <th className="col-filler" aria-hidden="true" />}
              </tr>
            </thead>
            <tbody>
              {sortedRows && sortedRows.length > 0 ? (
                sortedRows.map((row, index) => {
                  const eventDetails = row?.eventDetails || {};

                  // Host name using same rules as dropdown (first name, suffix rules, special name)
                  const hostDisplayName = formatPersonName({
                    first_name: row?.host_first_name,
                    member_first_name: row?.host_first_name,
                    name: row?.host_first_name || "",
                    suffix: row?.host_suffix,
                    special_name: row?.host_special_name,
                  });

                  const eventCreatorLabel = getEventCreatorColumnLabel(row);

                  const inviteesCell = (
                    <td className="col-invitees">
                      {renderNameWithColor(
                        formatPersonName(row),
                        row?.hex_code || "#000000",
                        row?.color_name ? `${row.color_name} Line` : ""
                      )}
                    </td>
                  );

                  return (
                    <tr key={row.id || row.invitation_id || index}>
                      <td className="col-host">
                        {renderNameWithColor(
                          hostDisplayName || row?.host_name || "-",
                          row?.host_hex_code || row?.hex_code || "#000000",
                          row?.host_color_name
                            ? `${row.host_color_name} Line`
                            : row?.color_name
                            ? `${row.color_name} Line`
                            : ""
                        )}
                      </td>
                      <td className="col-creator">{eventCreatorLabel}</td>
                      {!isMobileLayout && inviteesCell}
                      <td
                        className="col-event-name"
                        title={row?.event_name ?? undefined}
                        style={{ transform: "translateX(-8px)" }}
                      >
                        {row?.event_name ?? "-"}
                      </td>
                      <td className="col-event-date" style={{ transform: "translateX(-32px)" }}>
                        {(() => {
                          const start = eventDetails?.start_date;
                          const end = eventDetails?.end_date;
                          if (!start) return "-";
                          const startFormatted = formatDateShort(start);
                          if (end && String(end).trim() !== "") {
                            const endFormatted = formatDateShort(end);
                            return `${startFormatted} – ${endFormatted}`;
                          }
                          return startFormatted;
                        })()}
                      </td>
                      <td className="col-event-link">
                        {row?.eventDetails ? (
                          <a
                            href={row.event_link || "#"}
                            target={row.event_link ? "_blank" : undefined}
                            rel={row.event_link ? "noopener noreferrer" : undefined}
                            style={{
                              color: "#1890ff",
                              textDecoration: "underline",
                              wordBreak: "break-all",
                              cursor: "pointer",
                            }}
                            onClick={(e) => handleEventLinkClick(e, row)}
                          >
                            View
                          </a>
                        ) : (
                          "-"
                        )}
                      </td>
                      <td className="col-rsvp">
                        {row?.Rsvp_deadline
                          ? formatDateShort(row.Rsvp_deadline)
                          : "-"}
                      </td>
                      <td className="col-status">
                        {renderStatusBadge(row.invitation_status)}
                      </td>
                      {isMobileLayout && inviteesCell}
                      {!isMobileLayout && <td className="col-filler" aria-hidden="true" />}
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={isMobileLayout ? 8 : 9} className="text-center py-4">
                    No records found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          </div>
        </div>
      </div>
      <Modal
        isOpen={showEventModal}
        onRequestClose={() => setShowEventModal(false)}
        contentLabel="Event Details"
        overlayClassName="popup-overlay invite-overlay"
        portalClassName="event-moodal popup-card addmember-card event-detail-only-modal"
        ariaHideApp={false}
      >
        {selectedEvent && (
          <div className="view-event event-detail-modal-content">
            <div className="event-card event-detail-modal-card">
              <button
                type="button"
                className="event-detail-modal-close-btn"
                onClick={() => setShowEventModal(false)}
                aria-label="Close"
              >
                ×
              </button>
              <img
                src={
                  selectedEvent?.image ||
                  window.getPublicUrl("/assets/images/card-5.svg")
                }
                className="event-img"
                alt="Event"
              />
              <div className="card-body">
                <div className={`event-details-formatted${isFuneralEvent(selectedEvent) ? " funeral-flyer" : ""}`}>
                  {isFuneralEvent(selectedEvent) ? (
                    <FuneralHostLine event={selectedEvent} formatName={formatMemberName} />
                  ) : (
                    <>
                      {(selectedEvent?.Host || selectedEvent?.host_name) && (
                        <div className="event-detail-row">
                          <span className="event-detail-label">Host:</span>
                          <span className="event-detail-value">
                            {formatHostName(selectedEvent)}
                          </span>
                        </div>
                      )}
                      {selectedEvent?.CoHost && (
                        <div className="event-detail-row">
                          <span className="event-detail-label">Co-Host:</span>
                          <span className="event-detail-value">
                            {formatMemberName(selectedEvent.CoHost)}
                          </span>
                        </div>
                      )}
                      <div className="event-detail-row">
                        <span className="event-detail-label">Line Color:</span>
                        <span className="event-detail-value">
                          {(() => {
                            const hex =
                              selectedEvent?.Host?.hex_code ||
                              selectedEvent?.host_hex_code ||
                              selectedEvent?.hex_code ||
                              "#000000";
                            const lineColorName =
                              selectedEvent?.Host?.color_name ||
                              selectedEvent?.host_color_name ||
                              selectedEvent?.color_name ||
                              "";

                            if (!lineColorName) return "-";

                            return (
                              <span style={{ color: hex }}>
                                {lineColorName}
                              </span>
                            );
                          })()}
                        </span>
                      </div>
                    </>
                  )}
                  <div className="event-detail-row">
                    <span className="event-detail-label">Name of Event:</span>
                    <span className="event-detail-value">
                      {selectedEvent?.title || "-"}
                    </span>
                  </div>
                  <div className="event-detail-row">
                    <span className="event-detail-label">Event Date:</span>
                    <span className="event-detail-value">
                      {selectedEvent?.start_date
                        ? formatDateShort(selectedEvent.start_date)
                        : "-"}
                    </span>
                  </div>
                  <div className="event-detail-row">
                    <span className="event-detail-label">Time:</span>
                    <span className="event-detail-value">
                      {formatTime12Hour(selectedEvent?.event_time) || "-"}
                    </span>
                  </div>
                  {selectedEvent?.venue && (
                    <div className="event-detail-row">
                      <span className="event-detail-label">Venue:</span>
                      <span className="event-detail-value">
                        {selectedEvent.venue}
                      </span>
                    </div>
                  )}
                  <div className="event-detail-row">
                    <span className="event-detail-label">Location:</span>
                    <span className="event-detail-value">
                      {selectedEvent?.state && selectedEvent?.location
                        ? `${selectedEvent.location}, ${selectedEvent.state}`
                        : selectedEvent?.location ||
                          selectedEvent?.state ||
                          "-"}
                    </span>
                  </div>
                  {selectedEvent?.document && (
                    <div className="event-detail-row">
                      <span className="event-detail-label">View Add'l Details:</span>
                      <span className="event-detail-value">
                        <a
                          href={selectedEvent.document}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            color: "#1890ff",
                            textDecoration: "underline",
                            wordBreak: "break-all",
                          }}
                        >
                          View file
                        </a>
                      </span>
                    </div>
                  )}
                  <SpecialInstructionsDisplay
                    text={selectedEvent?.description}
                    compact={false}
                  />
                  {selectedEvent?.Rsvp_deadline && (
                    <div className="event-detail-row">
                      <span className="event-detail-label">RSVP Deadline:</span>
                      <span className="event-detail-value">
                        {isFuneralEvent(selectedEvent) && "RSVP by "}
                        {formatDateShort(selectedEvent.Rsvp_deadline)}
                      </span>
                    </div>
                  )}
                  <div className="event-detail-row event-invitation-status-section">
                    <div className="event-invitation-status-title">
                      {isFuneralEvent(selectedEvent) ? "Notice" : "Invitation Status"}
                    </div>
                    <div className="event-invitation-status-counts">
                      {isFuneralEvent(selectedEvent) ? (
                        <>
                          <strong>{selectedEvent?.total_invited ?? (selectedEvent?.invited_members || []).length}</strong> Notice(s) Sent
                        </>
                      ) : (
                        <>
                          {(() => {
                            const invited = selectedEvent?.invited_members || [];
                            const totalInvited = selectedEvent?.total_invited ?? invited.length;
                            const acceptedCount = selectedEvent?.accepted_count ?? invited.filter(
                              (inv) =>
                                inv?.invitation_status === 1 ||
                                String(inv?.invitation_status).toLowerCase() === "accepted"
                            ).length;
                            const declinedCount = selectedEvent?.declined_count ?? invited.filter(
                              (inv) =>
                                inv?.invitation_status === 2 ||
                                String(inv?.invitation_status).toLowerCase() === "declined"
                            ).length;
                            const pendingCount = selectedEvent?.pending_count ?? invited.filter(
                              (inv) =>
                                inv?.invitation_status === 0 ||
                                String(inv?.invitation_status).toLowerCase() === "pending"
                            ).length;
                            return (
                              <>
                                <strong>{totalInvited}</strong> Invited<span className="event-invitation-status-divider">|</span><strong>{acceptedCount}</strong> Accepted<span className="event-invitation-status-divider">|</span><strong>{declinedCount}</strong> Declined<span className="event-invitation-status-divider">|</span><strong>{pendingCount}</strong> Pending
                              </>
                            );
                          })()}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default AllTreeEvents;

